import { i18n } from '@/shared/i18n';
import type { ParseKeys } from 'i18next';

const t = (key: ParseKeys) => i18n.t(key, { ns: 'common' });

interface GqlErrorLike {
  message?: string;
  extensions?: {
    code?: string;
    statusCode?: number;
    response?: { status?: number; body?: { error?: string; message?: string } };
  };
}

interface ApolloErrorLike {
  message?: string;
  statusCode?: number;
  networkError?: Error | { statusCode?: number; message?: string } | null;
  graphQLErrors?: readonly GqlErrorLike[];
  errors?: readonly GqlErrorLike[];
}

const NETWORK_STATUS_CODES = [400, 401, 403, 404, 408, 409, 422, 429, 500, 502, 503, 504] as const;

type NetworkStatusCode = (typeof NETWORK_STATUS_CODES)[number];

const networkMessage = (status: number): string | undefined => {
  if ((NETWORK_STATUS_CODES as readonly number[]).includes(status)) {
    return t(`error.network.${status as NetworkStatusCode}`);
  }
  return undefined;
};

const TECHNICAL_MESSAGE =
  /response not successful|status code \d{3}|upstream http error|unexpected token|^unexpected error\.?$|econnrefused|network ?error|load failed/i;

export const isTechnicalMessage = (message: string): boolean => TECHNICAL_MESSAGE.test(message);

export const sanitizeMessage = (message: string): string => {
  if (!isTechnicalMessage(message)) {
    return message;
  }
  const status = Number(/\b([45]\d\d)\b/.exec(message)?.[1]);
  return networkMessage(status) ?? t('error.network.default');
};

const extractStatus = (err: ApolloErrorLike): number | undefined => {
  if (typeof err.statusCode === 'number') {
    return err.statusCode;
  }

  const net = err.networkError;
  if (net && typeof net === 'object' && 'statusCode' in net && typeof net.statusCode === 'number') {
    return net.statusCode;
  }

  const gql = err.graphQLErrors?.[0] ?? err.errors?.[0];
  return gql?.extensions?.statusCode ?? gql?.extensions?.response?.status;
};

const isOffline = () =>
  typeof navigator !== 'undefined' && typeof navigator.onLine === 'boolean' && !navigator.onLine;

export const formatError = (err: unknown): string => {
  if (err == null) {
    return t('error.generic');
  }

  if (isOffline()) {
    return t('error.network.offline');
  }

  if (typeof err === 'string') {
    return err.trim() || t('error.generic');
  }

  let gqlMessage: string | undefined;

  if (typeof err === 'object') {
    const apollo = err as ApolloErrorLike;

    if (
      apollo.networkError &&
      /failed to fetch|networkerror|err_(internet|network)/i.test(apollo.networkError.message ?? '')
    ) {
      return t('error.network.unreachable');
    }

    const status = extractStatus(apollo);
    const statusMessage = status === undefined ? undefined : networkMessage(status);
    if (statusMessage) {
      return statusMessage;
    }

    const gql = apollo.graphQLErrors?.[0] ?? apollo.errors?.[0];
    if (gql?.message) {
      if (
        gql.extensions?.code === 'BAD_USER_INPUT' ||
        gql.extensions?.code === 'FAILED_PRECONDITION'
      ) {
        return gql.message;
      }
      if (gql.extensions?.code === 'FORBIDDEN') {
        return t('error.network.403');
      }
      if (gql.extensions?.code === 'UNAUTHENTICATED') {
        return t('error.network.401');
      }
      gqlMessage = gql.message;
    }

    const upstreamBody = gql?.extensions?.response?.body;
    if (upstreamBody?.error) {
      return sanitizeMessage(upstreamBody.error);
    }
    if (upstreamBody?.message) {
      return sanitizeMessage(upstreamBody.message);
    }

    if (apollo.networkError?.message && /timeout|timed?\s*out/i.test(apollo.networkError.message)) {
      return t('error.network.timeout');
    }
  }

  const message = (err as { message?: unknown }).message;
  if (typeof message === 'string' && message.trim()) {
    if (/failed to fetch|networkerror|err_(internet|network)/i.test(message)) {
      return t('error.network.unreachable');
    }
    if (/timeout|timed?\s*out/i.test(message)) {
      return t('error.network.timeout');
    }
    if (/load failed/i.test(message)) {
      return t('error.network.loadFailed');
    }

    if (gqlMessage) {
      return sanitizeMessage(gqlMessage);
    }
    return sanitizeMessage(message.trim());
  }

  console.error('formatError: unrecognized error shape', err);
  return t('error.network.default');
};
